import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { lastSeenText } from '../lib/format.ts'
import { useAuth } from '../store/auth.ts'
import { useChats } from '../store/chats.ts'
import type { ChatDetail } from '../api/types.ts'

/**
 * СВЕДЕНИЯ О ПЕРЕПИСКЕ
 *
 * Здесь же всё, что когда-либо присылали: медиа, файлы, голосовые, ссылки.
 * Через полгода найти «ту фотографию», листая всю историю, невозможно —
 * а по вкладке это дело десяти секунд.
 */

type MediaItem = {
  id: string
  messageId: string
  fileName: string
  mimeType: string
  sizeBytes: number
  durationSec: number | null
  createdAt: string
  url: string
  thumbUrl: string | null
}

type LinkItem = {
  messageId: string
  url: string
  authorName: string | null
  createdAt: string
}

type Tab = 'media' | 'files' | 'voice' | 'links'

const TAB_NAMES: Record<Tab, string> = {
  media: 'Медиа',
  files: 'Файлы',
  voice: 'Голосовые',
  links: 'Ссылки',
}

const sizeText = (value: number): string =>
  value < 1024 * 1024 ? `${Math.round(value / 1024)} КБ` : `${(value / 1024 / 1024).toFixed(1)} МБ`

export function ChatInfo({
  chat,
  onClose,
  onJumpTo,
}: {
  chat: ChatDetail
  onClose: () => void
  onJumpTo: (messageId: string) => void
}) {
  const me = useAuth((s) => s.user)
  const loadChats = useChats((s) => s.loadChats)
  const clearChat = useChats((s) => s.clearChat)
  const removeChat = useChats((s) => s.removeChat)

  const [tab, setTab] = useState<Tab>('media')
  const [items, setItems] = useState<MediaItem[]>([])
  const [links, setLinks] = useState<LinkItem[]>([])
  const [loading, setLoading] = useState(false)

  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(chat.title)
  const [description, setDescription] = useState(chat.description ?? '')
  const [error, setError] = useState<string | null>(null)

  const isGroup = chat.kind !== 'DIRECT'
  const iAmAdmin = chat.members.find((m) => m.id === me?.id)?.isAdmin ?? false

  useEffect(() => {
    setLoading(true)
    api<{ items?: MediaItem[]; links?: LinkItem[] }>(`/api/chats/${chat.id}/media?kind=${tab}`)
      .then((data) => {
        setItems(data.items ?? [])
        setLinks(data.links ?? [])
      })
      .catch(() => {
        setItems([])
        setLinks([])
      })
      .finally(() => setLoading(false))
  }, [chat.id, tab])

  const saveGroup = async () => {
    setError(null)
    try {
      await api(`/api/chats/${chat.id}`, {
        method: 'PATCH',
        body: { title: title.trim(), description: description.trim() || null },
      })
      setEditing(false)
      await loadChats()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось сохранить')
    }
  }

  const removeMember = async (memberId: string, name: string) => {
    const leaving = memberId === me?.id
    const question = leaving
      ? 'Выйти из группы? Переписка останется, но новых сообщений ты не увидишь.'
      : `Исключить ${name} из группы?`
    if (!window.confirm(question)) return

    try {
      await api(`/api/chats/${chat.id}/members/${memberId}`, { method: 'DELETE' })
      await loadChats()
      if (leaving) onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    }
  }

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body is-wide" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{chat.title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {isGroup && !editing && (
          <>
            {chat.description && <p className="sheet-text">{chat.description}</p>}
            {iAmAdmin && (
              <button className="link-btn" onClick={() => setEditing(true)}>
                Изменить название и описание
              </button>
            )}
          </>
        )}

        {isGroup && editing && (
          <>
            <label className="field">
              <span className="field-label">Название</span>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={64} />
            </label>
            <label className="field">
              <span className="field-label">Описание</span>
              <textarea
                className="input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={400}
                placeholder="Для чего эта группа"
              />
            </label>
            <div className="sheet-actions">
              <button className="btn-primary" onClick={() => void saveGroup()}>
                Сохранить
              </button>
              <button className="btn-quiet" onClick={() => setEditing(false)}>
                Отмена
              </button>
            </div>
          </>
        )}

        <h3 className="sheet-sub">
          {isGroup ? `Участники · ${chat.members.length}` : 'Собеседник'}
        </h3>

        <div className="member-list">
          {chat.members.map((member) => (
            <div key={member.id} className="member-row">
              <Avatar
                id={member.id}
                name={member.displayName}
                url={member.avatarUrl}
                size={38}
                online={member.online}
              />
              <div className="member-text">
                <div className="member-name">
                  {member.displayName}
                  {member.isAdmin && <span className="tag">админ</span>}
                  {member.id === me?.id && <span className="tag">это ты</span>}
                </div>
                <div className="member-sub">{lastSeenText(member.lastSeenAt, member.online)}</div>
              </div>

              {isGroup && (iAmAdmin || member.id === me?.id) && (
                <button
                  className="member-remove"
                  onClick={() => void removeMember(member.id, member.displayName)}
                  title={member.id === me?.id ? 'Выйти из группы' : 'Исключить'}
                >
                  {member.id === me?.id ? 'выйти' : '✕'}
                </button>
              )}
            </div>
          ))}
        </div>

        {/**
         * Очистка и удаление живут здесь, а не только в меню строки
         * списка: на телефоне до того меню ещё надо додуматься —
         * долгое нажатие подсказки не даёт.
         */}
        <div className="info-actions">
          <button
            className="info-action"
            onClick={() => {
              const warning =
                'Очистить историю? У собеседника она останется: это её половина, а не твоя.'
              if (window.confirm(warning)) void clearChat(chat.id).then(onClose)
            }}
          >
            Очистить историю
          </button>

          {chat.kind !== 'SAVED' && (
            <button
              className="info-action is-danger"
              onClick={() => {
                const group = chat.kind === 'GROUP'
                const warning = group
                  ? 'Выйти из группы? Остальные увидят, что ты вышел.'
                  : 'Убрать переписку? Она вернётся, когда тебе напишут.'
                if (window.confirm(warning)) void removeChat(chat.id).then(onClose)
              }}
            >
              {chat.kind === 'GROUP' ? 'Выйти из группы' : 'Убрать переписку'}
            </button>
          )}
        </div>

        <div className="info-tabs">
          {(Object.keys(TAB_NAMES) as Tab[]).map((name) => (
            <button
              key={name}
              className={'info-tab' + (tab === name ? ' is-active' : '')}
              onClick={() => setTab(name)}
            >
              {TAB_NAMES[name]}
            </button>
          ))}
        </div>

        {loading && (
          <div className="center-screen" style={{ height: 90 }}>
            <div className="spinner" />
          </div>
        )}

        {!loading && tab === 'media' && (
          items.length === 0 ? (
            <p className="empty-hint">Ничего не присылали</p>
          ) : (
            <div className="info-grid">
              {items.map((item) => (
                <button
                  key={item.id}
                  className="info-thumb"
                  onClick={() => {
                    onJumpTo(item.messageId)
                    onClose()
                  }}
                  title="Перейти к сообщению"
                >
                  <img src={item.thumbUrl ?? item.url} alt={item.fileName} loading="lazy" />
                </button>
              ))}
            </div>
          )
        )}

        {!loading && (tab === 'files' || tab === 'voice') && (
          items.length === 0 ? (
            <p className="empty-hint">Ничего не присылали</p>
          ) : (
            <div className="info-list">
              {items.map((item) => (
                <button
                  key={item.id}
                  className="info-row"
                  onClick={() => {
                    onJumpTo(item.messageId)
                    onClose()
                  }}
                >
                  <span className="info-row-icon">{tab === 'voice' ? '🎤' : '📎'}</span>
                  <span className="info-row-text">
                    <span className="info-row-name">
                      {tab === 'voice'
                        ? `Голосовое · ${item.durationSec ?? 0} с`
                        : item.fileName}
                    </span>
                    <span className="info-row-sub">{sizeText(item.sizeBytes)}</span>
                  </span>
                </button>
              ))}
            </div>
          )
        )}

        {!loading && tab === 'links' && (
          links.length === 0 ? (
            <p className="empty-hint">Ссылок не было</p>
          ) : (
            <div className="info-list">
              {links.map((link, index) => (
                <div key={index} className="info-row">
                  <span className="info-row-icon">🔗</span>
                  <span className="info-row-text">
                    <a
                      className="info-row-name msg-link"
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.url}
                    </a>
                    <span className="info-row-sub">{link.authorName}</span>
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
